import { useState, useRef } from 'react';
import { lockScroll, unlockScroll } from '../utils/scrollLock';
import { buildCompositeLithologyName } from '../firebase/dictionaries';

export function useBoreholes({ boreholes, setBoreholes, getSvgCoordinates, scale, horizontalScale }) {
  // --- States ---
  const [expandedBoreholes, setExpandedBoreholes] = useState({});
  const [resizingLayer, setResizingLayer] = useState(null);
  const [resizeStart, setResizeStart] = useState(null);
  const [hasMovedDuringResize, setHasMovedDuringResize] = useState(false);
  const [draggingBorehole, setDraggingBorehole] = useState(null);
  const [layerInteractionStart, setLayerInteractionStart] = useState(null);
  const [editingLayerDialog, setEditingLayerDialog] = useState(null);
  const [draggingWaterLevel, setDraggingWaterLevel] = useState(null);
  const [mergeLayerSelection, setMergeLayerSelection] = useState({ boreholeId: null, layerIds: [] });
  const [mergeLayerDialog, setMergeLayerDialog] = useState(null);
  const [expandedLayers, setExpandedLayers] = useState({}); // { [boreholeId-layerId]: true }

  // --- Refs ---
  const lastClickTimeRef = useRef(0);
  const lastClickedLayerRef = useRef(null);
  const nameInputRef = useRef(null);
  const thicknessInputRef = useRef(null);

  // ===== Borehole CRUD =====

  const addBorehole = () => {
    const newId = Math.max(0, ...boreholes.map(b => b.id)) + 1;
    const lastDistance = boreholes.length > 0 ? boreholes[boreholes.length - 1].distance : 0;
    setBoreholes([...boreholes, {
      id: newId,
      name: `Otwór ${newId}`,
      depth: 217.0,
      distance: lastDistance + 100,
      waterLevels: [], // Poziomy wody gruntowej
      layers: [
        { id: 1, name: 'Warstwa 1', thickness: 5, color: '#8B4513', pattern: 'none', soilState: '', geotechnicalLayer: '' }
      ]
    }]);
  };

  const addBoreholeFromMap = (x, y) => {
    const newId = Math.max(0, ...boreholes.map(b => b.id)) + 1;
    const lastDistance = boreholes.length > 0 ? boreholes[boreholes.length - 1].distance : 0;
    setBoreholes([...boreholes, {
      id: newId,
      name: `Otwór ${newId}`,
      depth: 217.0,
      distance: lastDistance + 100,
      x: x,
      y: y,
      waterLevels: [], // Poziomy wody gruntowej
      layers: [
        { id: 1, name: 'Warstwa 1', thickness: 5, color: '#8B4513', pattern: 'none', soilState: '', geotechnicalLayer: '' }
      ]
    }]);
    // Uwaga: setAddBoreholeFromMapMode(false) musi być wywołane przez komponent nadrzędny
  };

  const addBoreholeAfter = (afterId) => {
    const currentIndex = boreholes.findIndex(b => b.id === afterId);
    if (currentIndex === -1) return;

    const currentBorehole = boreholes[currentIndex];
    const nextBorehole = boreholes[currentIndex + 1];

    let newDistance;
    if (nextBorehole) {
      newDistance = (currentBorehole.distance + nextBorehole.distance) / 2;
    } else {
      newDistance = currentBorehole.distance + 100;
    }

    const newId = Math.max(0, ...boreholes.map(b => b.id)) + 1;
    const newBorehole = {
      id: newId,
      name: `Otwór ${newId}`,
      depth: 217.0,
      distance: newDistance,
      waterLevels: [], // Poziomy wody gruntowej
      layers: [
        { id: 1, name: 'Warstwa 1', thickness: 5, color: '#8B4513', pattern: 'none', soilState: '', geotechnicalLayer: '' }
      ]
    };

    const newBoreholes = [...boreholes];
    newBoreholes.splice(currentIndex + 1, 0, newBorehole);
    setBoreholes(newBoreholes);
  };

  const moveBoreholeUp = (id) => {
    const currentIndex = boreholes.findIndex(b => b.id === id);
    if (currentIndex <= 0) return;

    const newBoreholes = [...boreholes];
    [newBoreholes[currentIndex - 1], newBoreholes[currentIndex]] =
    [newBoreholes[currentIndex], newBoreholes[currentIndex - 1]];
    setBoreholes(newBoreholes);
  };

  const moveBoreholeDown = (id) => {
    const currentIndex = boreholes.findIndex(b => b.id === id);
    if (currentIndex === -1 || currentIndex >= boreholes.length - 1) return;

    const newBoreholes = [...boreholes];
    [newBoreholes[currentIndex], newBoreholes[currentIndex + 1]] =
    [newBoreholes[currentIndex + 1], newBoreholes[currentIndex]];
    setBoreholes(newBoreholes);
  };

  const removeBorehole = (id) => {
    setBoreholes(boreholes.filter(b => b.id !== id));
  };

  const updateBorehole = (id, field, value) => {
    setBoreholes(boreholes.map(b =>
      b.id === id ? { ...b, [field]: value } : b
    ));
  };

  // ===== Layer CRUD =====

  const addLayer = (boreholeId) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        const newLayerId = Math.max(0, ...b.layers.map(l => l.id)) + 1;
        return {
          ...b,
          layers: [...b.layers, {
            id: newLayerId,
            name: `Warstwa ${newLayerId}`,
            thickness: 2,
            color: '#' + Math.floor(Math.random()*16777215).toString(16),
            pattern: 'none',
            soilState: '',
            geotechnicalLayer: ''
          }]
        };
      }
      return b;
    }));
  };

  const addLayerAfter = (boreholeId, afterIndex) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        const newLayerId = Math.max(0, ...b.layers.map(l => l.id)) + 1;
        const newLayer = {
          id: newLayerId,
          name: `Warstwa ${newLayerId}`,
          thickness: 2,
          color: '#' + Math.floor(Math.random()*16777215).toString(16),
          pattern: 'none',
          soilState: '',
          geotechnicalLayer: ''
        };
        const newLayers = [...b.layers];
        newLayers.splice(afterIndex + 1, 0, newLayer);
        return { ...b, layers: newLayers };
      }
      return b;
    }));
  };

  const moveLayerUp = (boreholeId, layerIndex) => {
    if (layerIndex <= 0) return;
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        const newLayers = [...b.layers];
        [newLayers[layerIndex - 1], newLayers[layerIndex]] =
        [newLayers[layerIndex], newLayers[layerIndex - 1]];
        return { ...b, layers: newLayers };
      }
      return b;
    }));
  };

  const moveLayerDown = (boreholeId, layerIndex) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        if (layerIndex >= b.layers.length - 1) return b;
        const newLayers = [...b.layers];
        [newLayers[layerIndex], newLayers[layerIndex + 1]] =
        [newLayers[layerIndex + 1], newLayers[layerIndex]];
        return { ...b, layers: newLayers };
      }
      return b;
    }));
  };

  const removeLayer = (boreholeId, layerId) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        return { ...b, layers: b.layers.filter(l => l.id !== layerId) };
      }
      return b;
    }));
  };

  // ===== Merge Layers (trwałe scalenie warstw w jednym otworze) =====

  const toggleLayerMergeSelection = (boreholeId, layerId) => {
    setMergeLayerSelection(prev => {
      if (prev.boreholeId !== boreholeId) {
        return { boreholeId, layerIds: [layerId] };
      }
      const exists = prev.layerIds.includes(layerId);
      const next = exists
        ? prev.layerIds.filter(id => id !== layerId)
        : [...prev.layerIds, layerId];
      return next.length === 0
        ? { boreholeId: null, layerIds: [] }
        : { boreholeId, layerIds: next };
    });
  };

  const clearMergeLayerSelection = () => {
    setMergeLayerSelection({ boreholeId: null, layerIds: [] });
  };

  const isMergeSelectionContiguous = (boreholeId, layerIds) => {
    if (layerIds.length < 2) return false;
    const borehole = boreholes.find(b => b.id === boreholeId);
    if (!borehole) return false;
    const indices = layerIds
      .map(id => borehole.layers.findIndex(l => l.id === id))
      .filter(i => i >= 0)
      .sort((a, b) => a - b);
    if (indices.length !== layerIds.length) return false;
    for (let i = 1; i < indices.length; i++) {
      if (indices[i] !== indices[i - 1] + 1) return false;
    }
    return true;
  };

  const openMergeLayerDialog = () => {
    const { boreholeId, layerIds } = mergeLayerSelection;
    if (!isMergeSelectionContiguous(boreholeId, layerIds)) return;
    setMergeLayerDialog({ boreholeId, layerIds: [...layerIds], masterId: layerIds[0] });
  };

  const mergeLayers = (boreholeId, layerIds, masterId) => {
    if (!isMergeSelectionContiguous(boreholeId, layerIds)) return;
    const roundCm = (v) => Math.round((Number(v) || 0) * 100) / 100;

    setBoreholes(prev => prev.map(b => {
      if (b.id !== boreholeId) return b;

      const sortedGroup = layerIds
        .map(id => b.layers.find(l => l.id === id))
        .filter(Boolean)
        .sort((a, b2) => b.layers.indexOf(a) - b.layers.indexOf(b2));

      if (sortedGroup.length < 2) return b;

      const master = sortedGroup.find(l => l.id === masterId) || sortedGroup[0];
      const firstIdx = b.layers.indexOf(sortedGroup[0]);
      const sumThickness = roundCm(sortedGroup.reduce((s, l) => s + (Number(l.thickness) || 0), 0));

      const hasFromTop = sortedGroup[0].from != null;
      const newFrom = hasFromTop ? roundCm(sortedGroup[0].from) : undefined;
      const newTo = hasFromTop ? roundCm(newFrom + sumThickness) : undefined;

      const merged = {
        ...master,
        id: master.id,
        thickness: sumThickness,
      };
      if (hasFromTop) {
        merged.from = newFrom;
        merged.to = newTo;
      } else {
        delete merged.from;
        delete merged.to;
      }

      const newLayers = [...b.layers];
      newLayers.splice(firstIdx, sortedGroup.length, merged);
      return { ...b, layers: newLayers };
    }));

    setMergeLayerDialog(null);
    setMergeLayerSelection({ boreholeId: null, layerIds: [] });
  };

  const updateLayerFields = (boreholeId, layerId, patch) => {
    setBoreholes(prev => prev.map(b => {
      if (b.id !== boreholeId) return b;
      return {
        ...b,
        layers: b.layers.map(l => l.id === layerId ? { ...l, ...patch } : l)
      };
    }));
  };

  const toggleLayerExpanded = (boreholeId, layerId) => {
    const key = `${boreholeId}-${layerId}`;
    setExpandedLayers(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  const isLayerExpanded = (boreholeId, layerId) => !!expandedLayers[`${boreholeId}-${layerId}`];

  const updateLayer = (boreholeId, layerId, field, value) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        return {
          ...b,
          layers: b.layers.map(l =>
            l.id === layerId ? { ...l, [field]: value } : l
          )
        };
      }
      return b;
    }));

    if (field === 'color') {
      const targetLayer = boreholes
        .find(b => b.id === boreholeId)?.layers
        .find(l => l.id === layerId);

      if (targetLayer) {
        setBoreholes(prevBoreholes => prevBoreholes.map(b => ({
          ...b,
          layers: b.layers.map(l =>
            l.name === targetLayer.name ? { ...l, color: value } : l
          )
        })));
      }
    }

    if (field === 'name') {
      const existingLayer = boreholes
        .flatMap(b => b.layers)
        .find(l => l.name === value);

      if (existingLayer) {
        setBoreholes(prevBoreholes => prevBoreholes.map(b => ({
          ...b,
          layers: b.layers.map(l =>
            l.id === layerId && b.id === boreholeId
              ? { ...l, name: value, color: existingLayer.color }
              : l
          )
        })));
      }
    }
  };

  // ===== Layer Edit Dialog =====

  const openLayerEditDialog = (boreholeId, layerId) => {
    const borehole = boreholes.find(b => b.id === boreholeId);
    if (!borehole) return;
    const layer = borehole.layers.find(l => l.id === layerId);
    if (!layer) return;

    // From/To liczymy ZAWSZE na żywo z kumulatywnego thickness, ignorując zapisane layer.from/to.
    // Powód: drag warstwy (handleResizeMove) zmienia tylko thickness, więc layer.from/to po dragu są
    // niezsynchronizowane — dialog pokazywałby stare wartości.
    let cumulFrom = 0;
    for (const l of borehole.layers) {
      if (l.id === layerId) break;
      cumulFrom += Number(l.thickness || 0);
    }
    const cumulTo = cumulFrom + Number(layer.thickness || 0);
    const roundCm = (v) => Math.round(v * 100) / 100;

    setEditingLayerDialog({
      boreholeId,
      layerId,
      // Stare pola (modal nadal je pokazuje)
      name: layer.name,
      thickness: layer.thickness,
      soilState: layer.soilState || '',
      geotechnicalLayer: layer.geotechnicalLayer || '',
      pattern: layer.pattern || 'none',
      color: layer.color || '#8B4513',
      // Pelna litologia — świeże from/to z aktualnego thickness
      from: roundCm(cumulFrom),
      to: roundCm(cumulTo),
      materialType: layer.materialType || 'grunt',
      lithologyRockSoil01: layer.lithologyRockSoil01 || layer.name || '',
      lithologyRockSoilFullName01: layer.lithologyRockSoilFullName01 || '',
      layeringRockSoil01: layer.layeringRockSoil01 || '',
      lithologyRockSoil02: layer.lithologyRockSoil02 || '',
      lithologyRockSoilFullName02: layer.lithologyRockSoilFullName02 || '',
      layeringRockSoil02: layer.layeringRockSoil02 || '',
      lithologyRockSoil03: layer.lithologyRockSoil03 || '',
      lithologyRockSoilFullName03: layer.lithologyRockSoilFullName03 || '',
      genesisAndStratigraphy: layer.genesisAndStratigraphy || '',
    });

    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }
    }, 10);
  };

  const handleLayerDialogSubmit = () => {
    if (editingLayerDialog) {
      const d = editingLayerDialog;
      // Zaokraglenie do cm — eliminuje float-point smiec typu 5.3999999999999995
      const roundCm = (v) => Math.round((Number(v) || 0) * 100) / 100;
      const fromN = roundCm(d.from);
      const toN = roundCm(d.to);
      const computedThickness = roundCm(Math.max(0, toN - fromN));
      const composite = buildCompositeLithologyName(d);
      const displayName = composite || d.name || '';

      setBoreholes(boreholes.map(b => {
        if (b.id === d.boreholeId) {
          return {
            ...b,
            layers: b.layers.map(l =>
              l.id === d.layerId ? {
                ...l,
                name: displayName,
                compositeLithology: composite,
                thickness: computedThickness > 0 ? computedThickness : roundCm(parseFloat(d.thickness)),
                from: fromN,
                to: toN || roundCm(fromN + (parseFloat(d.thickness) || 0)),
                soilState: d.soilState,
                geotechnicalLayer: d.geotechnicalLayer,
                pattern: d.pattern,
                color: d.color,
                materialType: d.materialType || 'grunt',
                lithologyRockSoil01: d.lithologyRockSoil01 || '',
                lithologyRockSoilFullName01: d.lithologyRockSoilFullName01 || '',
                layeringRockSoil01: d.layeringRockSoil01 || '',
                lithologyRockSoil02: d.lithologyRockSoil02 || '',
                lithologyRockSoilFullName02: d.lithologyRockSoilFullName02 || '',
                layeringRockSoil02: d.layeringRockSoil02 || '',
                lithologyRockSoil03: d.lithologyRockSoil03 || '',
                lithologyRockSoilFullName03: d.lithologyRockSoilFullName03 || '',
                genesisAndStratigraphy: d.genesisAndStratigraphy || '',
              } : l
            )
          };
        }
        return b;
      }));
    }
    setEditingLayerDialog(null);
  };

  const handleLayerDialogCancel = () => {
    setEditingLayerDialog(null);
  };

  // ===== Water Level CRUD =====

  const addWaterLevel = (boreholeId, type) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        const newId = Math.max(0, ...b.waterLevels.map(w => w.id || 0)) + 1;
        const newWaterLevel = type === 'stabilized-drilled'
          ? {
              id: newId,
              type: 'stabilized-drilled',
              depthFrom: 2.0,
              depthTo: 4.0,
              label: `${b.waterLevels.length + 1}`
            }
          : {
              id: newId,
              type: 'seepage',
              depth: 3.0,
              label: `${b.waterLevels.length + 1}`
            };
        return { ...b, waterLevels: [...b.waterLevels, newWaterLevel] };
      }
      return b;
    }));
  };

  const removeWaterLevel = (boreholeId, waterLevelId) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        return { ...b, waterLevels: b.waterLevels.filter(w => w.id !== waterLevelId) };
      }
      return b;
    }));
  };

  const updateWaterLevel = (boreholeId, waterLevelId, field, value) => {
    setBoreholes(boreholes.map(b => {
      if (b.id === boreholeId) {
        return {
          ...b,
          waterLevels: b.waterLevels.map(w =>
            w.id === waterLevelId ? { ...w, [field]: value } : w
          )
        };
      }
      return b;
    }));
  };

  // ===== Borehole Drag (drag borehole header to change distance on SVG) =====

  const handleBoreholeHeaderDragStart = (e, boreholeId) => {
    // Najpierw pobierz clientX zanim wywołasz preventDefault
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;

    e.stopPropagation();
    e.preventDefault(); // Zapobiega domyślnemu zachowaniu na dotyk

    const borehole = boreholes.find(b => b.id === boreholeId);
    setDraggingBorehole({
      boreholeId,
      startX: clientX,
      originalDistance: borehole.distance
    });
  };

  const handleDragMove = (e) => {
    if (draggingBorehole) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - draggingBorehole.startX;
      const deltaMeters = deltaX / horizontalScale;

      setBoreholes(boreholes.map(b => {
        if (b.id === draggingBorehole.boreholeId) {
          const newDistance = Math.max(0, draggingBorehole.originalDistance + deltaMeters);
          return { ...b, distance: Math.round(newDistance) };
        }
        return b;
      }));
    }
  };

  const handleDragEnd = () => {
    setDraggingBorehole(null);
  };

  // ===== Layer Resize (drag to resize thickness on SVG) =====

  const handleResizeStart = (e, boreholeId, layerId) => {
    if (draggingBorehole) return;
    e.stopPropagation();
    e.preventDefault();
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setResizingLayer({ boreholeId, layerId });
    setResizeStart(clientY);
    setHasMovedDuringResize(false); // Resetuj flagę ruchu
    setLayerInteractionStart({ x: clientX, y: clientY, time: Date.now() });

    // Zablokuj przewijanie strony
    lockScroll();
  };

  const handleResizeMove = (e) => {
    if (resizingLayer && resizeStart) {
      e.preventDefault(); // Zapobiega przewijaniu
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const deltaY = clientY - resizeStart;

      // Jeśli jest jakikolwiek ruch większy niż tolerancja, oznacz to i aktualizuj
      if (Math.abs(deltaY) > 5) { // Tolerancja 5px na przypadkowe drgnięcia i podwójne kliknięcia
        setHasMovedDuringResize(true);

        const deltaMeters = deltaY / (scale / 10);

        setBoreholes(boreholes.map(b => {
          if (b.id === resizingLayer.boreholeId) {
            return {
              ...b,
              layers: b.layers.map(l => {
                if (l.id === resizingLayer.layerId) {
                  const newThickness = Math.max(0.1, l.thickness + deltaMeters);
                  return { ...l, thickness: parseFloat(newThickness.toFixed(2)) };
                }
                return l;
              })
            };
          }
          return b;
        }));

        setResizeStart(clientY);
      }
    }
  };

  const handleResizeEnd = () => {
    // Przywróć przewijanie strony i usuń padding
    unlockScroll();

    if (layerInteractionStart && resizingLayer) {
      const currentTime = Date.now();
      const timeDiff = currentTime - layerInteractionStart.time;

      // Sprawdź czy to było krótkie kliknięcie bez ruchu (potencjalnie część podwójnego kliknięcia)
      if (timeDiff < 300 && !hasMovedDuringResize) {
        const layerKey = `${resizingLayer.boreholeId}-${resizingLayer.layerId}`;
        const timeSinceLastClick = currentTime - lastClickTimeRef.current;

        // Jeśli to drugie kliknięcie w ciągu 500ms na tej samej warstwie - otwórz dialog edycji
        if (timeSinceLastClick < 500 && lastClickedLayerRef.current === layerKey) {
          openLayerEditDialog(resizingLayer.boreholeId, resizingLayer.layerId);
          // Zresetuj po otworzeniu edycji
          lastClickTimeRef.current = 0;
          lastClickedLayerRef.current = null;
        } else {
          // Pierwsze kliknięcie - zapamiętaj czas i warstwę
          lastClickTimeRef.current = currentTime;
          lastClickedLayerRef.current = layerKey;
        }
      } else {
        // Było przeciąganie - zresetuj licznik kliknięć
        lastClickTimeRef.current = 0;
        lastClickedLayerRef.current = null;
      }
    }

    setResizingLayer(null);
    setResizeStart(null);
    setHasMovedDuringResize(false);
    setLayerInteractionStart(null);
  };

  // ===== Water Level Mouse Handlers (drag on SVG) =====

  const handleWaterLevelMouseDown = (e, boreholeId, waterLevelId, type) => {
    e.stopPropagation();
    e.preventDefault();

    // Zablokuj przewijanie strony
    lockScroll();

    const { y } = getSvgCoordinates(e);
    const borehole = boreholes.find(b => b.id === boreholeId);
    const waterLevel = borehole?.waterLevels.find(wl => wl.id === waterLevelId);

    if (!borehole || !waterLevel) return;

    const pixelsPerMeter = scale / 10;
    const depthOffset = (borehole.depth - Math.max(...boreholes.map(b => b.depth))) * pixelsPerMeter;

    // Zapisz początkową głębokość i pozycję Y
    let initialDepth;
    if (type === 'seepage') {
      initialDepth = waterLevel.depth;
    } else if (type === 'stabilized') {
      initialDepth = waterLevel.depthFrom;
    } else if (type === 'drilled') {
      initialDepth = waterLevel.depthTo;
    } else if (type === 'both') {
      // Dla przeciągania obu - użyj średniej głębokości jako odniesienia
      initialDepth = (waterLevel.depthFrom + waterLevel.depthTo) / 2;
    }

    setDraggingWaterLevel({ boreholeId, waterLevelId, type, startY: y, initialDepth, depthOffset });
  };

  const handleWaterLevelMouseMove = (e) => {
    if (!draggingWaterLevel) return;
    e.preventDefault();

    const { y } = getSvgCoordinates(e);
    const pixelsPerMeter = scale / 10;

    // Oblicz różnicę w pozycji Y (delta)
    const deltaY = y - draggingWaterLevel.startY;
    const deltaDepth = deltaY / pixelsPerMeter;

    // Oblicz nową głębokość = początkowa głębokość + delta, zaokrąglona do 1 miejsca po przecinku
    const newDepth = Math.max(0, Math.round((draggingWaterLevel.initialDepth + deltaDepth) * 10) / 10);

    // Aktualizuj głębokość
    setBoreholes(boreholes.map(b => {
      if (b.id === draggingWaterLevel.boreholeId) {
        return {
          ...b,
          waterLevels: b.waterLevels.map(wl => {
            if (wl.id === draggingWaterLevel.waterLevelId) {
              if (draggingWaterLevel.type === 'seepage') {
                return { ...wl, depth: newDepth };
              } else if (draggingWaterLevel.type === 'stabilized') {
                return { ...wl, depthFrom: newDepth };
              } else if (draggingWaterLevel.type === 'drilled') {
                return { ...wl, depthTo: newDepth };
              } else if (draggingWaterLevel.type === 'both') {
                // Przeciągaj obie głębokości jednocześnie (ustabilizowane i nawiercone)
                // newDepth to nowa wartość średniej, więc obliczamy deltę i dodajemy do obu
                return {
                  ...wl,
                  depthFrom: Math.max(0, Math.round((wl.depthFrom + deltaDepth) * 10) / 10),
                  depthTo: Math.max(0, Math.round((wl.depthTo + deltaDepth) * 10) / 10)
                };
              }
            }
            return wl;
          })
        };
      }
      return b;
    }));
  };

  const handleWaterLevelMouseUp = () => {
    unlockScroll();

    setDraggingWaterLevel(null);
  };

  // ===== Return =====

  return {
    // States
    expandedBoreholes, setExpandedBoreholes,
    resizingLayer, setResizingLayer,
    resizeStart, setResizeStart,
    hasMovedDuringResize, setHasMovedDuringResize,
    draggingBorehole, setDraggingBorehole,
    layerInteractionStart, setLayerInteractionStart,
    editingLayerDialog, setEditingLayerDialog,
    draggingWaterLevel, setDraggingWaterLevel,

    // Refs
    lastClickTimeRef,
    lastClickedLayerRef,
    nameInputRef,
    thicknessInputRef,

    // Borehole CRUD
    addBorehole,
    addBoreholeFromMap,
    addBoreholeAfter,
    moveBoreholeUp,
    moveBoreholeDown,
    removeBorehole,
    updateBorehole,

    // Layer CRUD
    addLayer,
    addLayerAfter,
    moveLayerUp,
    moveLayerDown,
    removeLayer,
    updateLayer,

    // Bulk update + expand state
    updateLayerFields,
    expandedLayers, setExpandedLayers,
    toggleLayerExpanded,
    isLayerExpanded,

    // Merge layers
    mergeLayerSelection, setMergeLayerSelection,
    mergeLayerDialog, setMergeLayerDialog,
    toggleLayerMergeSelection,
    clearMergeLayerSelection,
    isMergeSelectionContiguous,
    openMergeLayerDialog,
    mergeLayers,

    // Layer Edit Dialog
    openLayerEditDialog,
    handleLayerDialogSubmit,
    handleLayerDialogCancel,

    // Water Level CRUD
    addWaterLevel,
    removeWaterLevel,
    updateWaterLevel,

    // Borehole drag handlers
    handleBoreholeHeaderDragStart,
    handleDragMove,
    handleDragEnd,

    // Resize handlers
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,

    // Water Level mouse handlers
    handleWaterLevelMouseDown,
    handleWaterLevelMouseMove,
    handleWaterLevelMouseUp,
  };
}
